package dev.akshaychordiya

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

/**
 * The site's shared motion vocabulary.
 *
 * Three rules hold everything together.
 *
 * Movement is small - nothing travels further than about 12px, because large motion on a text-heavy page reads as
 * noise rather than polish.
 *
 * Everything is opt-out: a global `prefers-reduced-motion` rule in the page head (see `build.gradle.kts`) collapses
 * every animation and transition here to effectively zero duration, so none of this needs to guard itself.
 *
 * And every entrance uses `fill-mode: backwards`, never `both`. `backwards` holds the *opening* frame during the
 * stagger delay (so nothing flashes in before its turn) but hands styling back to the element once the animation
 * ends. `both` would pin the closing frame permanently, and since these keyframes touch `opacity` and `transform`,
 * that would silently defeat anything else using those properties - a card's hover lift, or the map's filter
 * dimming. Entrances should get out of the way when they're done.
 */

/** Easing that starts quickly and settles gently - the workhorse for entrances. */
val EaseOutSoft = AnimationTimingFunction.cubicBezier(0.16, 1.0, 0.3, 1.0)

private const val EASE_OUT_SOFT_CSS = "cubic-bezier(0.16, 1, 0.3, 1)"

/** Content arriving: fade up into place. */
val FadeUpKeyframes = Keyframes {
    from { Modifier.opacity(0).translateY(12.px) }
    to { Modifier.opacity(1).translateY(0.px) }
}

/** A quieter entrance for text that shouldn't draw the eye away from the headline. */
val FadeInKeyframes = Keyframes {
    from { Modifier.opacity(0) }
    to { Modifier.opacity(1) }
}

/** Map dots landing: overshoot slightly, then settle. */
val PopInKeyframes = Keyframes {
    from { Modifier.opacity(0).scale(0.2) }
    60.percent { Modifier.opacity(1).scale(1.15) }
    to { Modifier.opacity(1).scale(1) }
}

/** One icon replacing another: turn in from a quarter rotation rather than snapping into place. */
val SwapInKeyframes = Keyframes {
    from { Modifier.opacity(0).rotate((-90).deg).scale(0.6) }
    to { Modifier.opacity(1).rotate(0.deg).scale(1) }
}

private const val STAGGER_STEP = 55
private const val MAX_STAGGER = 440

/** Each step adds [STAGGER_STEP], capped so a long list never leaves the reader waiting on the last item. */
fun staggerDelay(index: Int): CSSSizeValue<CSSUnit.ms> = (index * STAGGER_STEP).coerceAtMost(MAX_STAGGER).ms

/** Fade up, optionally staggered by position in a list. */
@Composable
fun Modifier.fadeUp(index: Int = 0, duration: Int = 520): Modifier = animation(
    FadeUpKeyframes.toAnimation(
        duration = duration.ms,
        timingFunction = EaseOutSoft,
        delay = staggerDelay(index),
        fillMode = AnimationFillMode.Backwards,
    )
)

@Composable
fun Modifier.fadeIn(index: Int = 0, duration: Int = 600): Modifier = animation(
    FadeInKeyframes.toAnimation(
        duration = duration.ms,
        timingFunction = EaseOutSoft,
        delay = staggerDelay(index),
        fillMode = AnimationFillMode.Backwards,
    )
)

@Composable
fun Modifier.popIn(index: Int = 0, duration: Int = 620): Modifier = animation(
    PopInKeyframes.toAnimation(
        duration = duration.ms,
        timingFunction = EaseOutSoft,
        delay = staggerDelay(index),
        fillMode = AnimationFillMode.Backwards,
    )
)

/**
 * No stagger and no index: this fires on a deliberate click, so there is nothing to queue behind.
 *
 * Only runs if the element is genuinely recreated - an animation applied to a node that merely recomposes will not
 * replay. The colour-mode button wraps its icon in a `key(colorMode)` for exactly that reason.
 */
@Composable
fun Modifier.swapIn(duration: Int = 360): Modifier = animation(
    SwapInKeyframes.toAnimation(
        duration = duration.ms,
        timingFunction = EaseOutSoft,
        fillMode = AnimationFillMode.Backwards,
    )
)

/**
 * Standard transition for interactive affordances - hover lifts, colour shifts, chip selection.
 *
 * Written as the CSS shorthand rather than through a typed builder so the property list stays readable at the call
 * site, and so it can be applied inside a `CssStyle` base block without pulling in extra machinery.
 */
fun Modifier.smoothTransition(vararg properties: String): Modifier = styleModifier {
    property("transition", properties.joinToString(", ") { "$it 200ms $EASE_OUT_SOFT_CSS" })
}
